import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Skeleton, EmptyState, DataTable } from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { userService } from '@/services/user.service';
import type { User, UserShift } from '@/types/user';

const PANEL_CLASS =
  'rounded-2xl border border-slate-200/80 bg-white shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)]';

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

function toneClasses(tone: 'primary' | 'success' | 'warning' | 'danger' | 'info') {
  switch (tone) {
    case 'success':
      return { icon: 'bg-emerald-50 text-emerald-600', accent: 'from-emerald-500/20 to-emerald-500/0' };
    case 'warning':
      return { icon: 'bg-amber-50 text-amber-600', accent: 'from-amber-500/18 to-amber-500/0' };
    case 'danger':
      return { icon: 'bg-rose-50 text-rose-600', accent: 'from-rose-500/18 to-rose-500/0' };
    case 'info':
      return { icon: 'bg-sky-50 text-sky-600', accent: 'from-sky-500/18 to-sky-500/0' };
    default:
      return { icon: 'bg-indigo-50 text-indigo-600', accent: 'from-indigo-500/18 to-indigo-500/0' };
  }
}

function PerfStatCard({
  label,
  value,
  supporting,
  tone,
  icon,
}: {
  label: string;
  value: string | number;
  supporting?: string;
  tone: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  icon: React.ReactNode;
}) {
  const palette = toneClasses(tone);
  return (
    <div className={cn(PANEL_CLASS, 'relative overflow-hidden p-5')}>
      <div className={cn('absolute inset-x-0 top-0 h-14 bg-gradient-to-b', palette.accent)} />
      <div className="relative space-y-3">
        <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', palette.icon)}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-950 tabular-nums">{value}</p>
        </div>
        {supporting && <p className="text-xs text-slate-400">{supporting}</p>}
      </div>
    </div>
  );
}

function formatShiftDate(row: UserShift): string {
  const source = row.clock_in ?? row.shift_date;
  if (!source) return '\u2014';
  const d = new Date(source);
  if (Number.isNaN(d.getTime())) return '\u2014';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusBadgeClass(status: UserShift['status']): string {
  switch (status) {
    case 'PRESENT':
      return 'bg-emerald-50 text-emerald-700';
    case 'LATE':
      return 'bg-amber-50 text-amber-700';
    case 'HALF_DAY':
      return 'bg-sky-50 text-sky-700';
    case 'ABSENT':
      return 'bg-rose-50 text-rose-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}

function makeShiftColumns(currency: CurrencyCode): ColumnDef<UserShift>[] {
  return [
  {
    key: 'shift_date',
    header: 'Date',
    render: (_v, row) => (
      <span className="text-sm text-slate-900 tabular-nums">{formatShiftDate(row)}</span>
    ),
  },
  {
    key: 'location_name',
    header: 'Location',
    render: (_v, row) => (
      <span className="text-sm text-slate-600">{row.location_name ?? '\u2014'}</span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => (
      <span className={cn(
        'inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold',
        statusBadgeClass(row.status),
      )}>
        {row.status ?? '\u2014'}
      </span>
    ),
  },
  {
    key: 'total_bills',
    header: 'Bills',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm text-slate-900 tabular-nums">{row.total_bills ?? 0}</span>
    ),
  },
  {
    key: 'total_sales',
    header: 'Sales',
    numeric: true,
    render: (_v, row) => (
      <span className="text-sm font-medium text-slate-900 tabular-nums">
        {formatPaise(row.total_sales ?? 0, currency)}
      </span>
    ),
  },
  ];
}

interface UserPerformanceTabProps {
  userId: string;
  user: User;
}

export function UserPerformanceTab({ userId, user }: UserPerformanceTabProps) {
  const currency = useTenantCurrency();
  const shiftColumns = useMemo(() => makeShiftColumns(currency), [currency]);
  const { data: performance, isLoading: perfLoading } = useQuery({
    queryKey: ['user', userId, 'performance'],
    queryFn: () => userService.getPerformance(userId),
    staleTime: 60_000,
  });

  const { data: shiftsData, isLoading: shiftsLoading } = useQuery({
    queryKey: ['user', userId, 'shifts', 'performance'],
    queryFn: () => userService.getShifts(userId, { limit: 20 }),
    staleTime: 30_000,
  });

  const shifts = shiftsData?.data ?? [];
  const isLoading = perfLoading || shiftsLoading;

  // Dev-only invariant: when every shift row is loaded (no pagination cut), the
  // sum of per-shift bills/sales must not exceed the user's KPI totals. Each
  // bill is attributed to at most one attendance row server-side, so SUM(shift)
  // <= KPI is the contract — equality holds when all bills have a matching shift.
  useEffect(() => {
    if (import.meta.env.PROD) return;
    if (!performance || !shiftsData) return;
    const total = shiftsData.meta?.total ?? shifts.length;
    if (total > shifts.length) return;
    const sumBills = shifts.reduce((acc, s) => acc + (s.total_bills ?? 0), 0);
    const sumSales = shifts.reduce((acc, s) => acc + (s.total_sales ?? 0), 0);
    if (sumBills > performance.total_bills) {
      console.warn(
        `[UserPerformanceTab] shift bills sum (${sumBills}) exceeds KPI total_bills (${performance.total_bills}) — double-counting regression`,
      );
    }
    if (sumSales > performance.total_sales) {
      console.warn(
        `[UserPerformanceTab] shift sales sum (${sumSales}) exceeds KPI total_sales (${performance.total_sales}) — double-counting regression`,
      );
    }
  }, [performance, shiftsData, shifts]);

  // Show relevant metrics based on role
  const isCashierOrCaptain = ['CASHIER', 'CAPTAIN'].includes(user.role);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={140} />
          ))}
        </div>
        <Skeleton variant="rect" height={300} />
      </div>
    );
  }

  const perf = performance ?? {
    total_sales: 0,
    total_bills: 0,
    avg_bill_value: 0,
    shifts_worked: 0,
    total_returns: 0,
    avg_items_per_bill: 0,
  };

  return (
    <div className="space-y-6">
      {/* KPI Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <PerfStatCard
          label="Total Sales"
          value={formatPaise(perf.total_sales, currency)}
          tone="primary"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
            </svg>
          }
        />
        <PerfStatCard
          label="Bills Processed"
          value={perf.total_bills.toLocaleString('en-IN')}
          tone="success"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
            </svg>
          }
        />
        <PerfStatCard
          label="Avg Bill Value"
          value={formatPaise(perf.avg_bill_value, currency)}
          tone="info"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
          }
        />
        <PerfStatCard
          label="Shifts Worked"
          value={perf.shifts_worked.toLocaleString('en-IN')}
          tone="warning"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* Additional metrics */}
      {isCashierOrCaptain && (
        <div className="grid grid-cols-2 gap-3">
          <PerfStatCard
            label="Returns"
            value={perf.total_returns.toLocaleString('en-IN')}
            supporting="Total return transactions"
            tone="danger"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
              </svg>
            }
          />
          <PerfStatCard
            label="Avg Items / Bill"
            value={perf.avg_items_per_bill.toFixed(1)}
            supporting="Average line items per transaction"
            tone="info"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
              </svg>
            }
          />
        </div>
      )}

      {/* Shift History Table */}
      <div className={cn(PANEL_CLASS)}>
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Shift History</h3>
              <p className="text-xs text-slate-500">Recent shift records and sales performance</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          {shifts.length > 0 ? (
            <DataTable<UserShift>
              columns={shiftColumns}
              data={shifts}
              loading={shiftsLoading}
              variant="compact"
              emptyState={{
                title: 'No shifts',
                description: 'No shift records found',
              }}
            />
          ) : (
            <EmptyState
              variant="inline"
              title="No shift history"
              description="Shift data will appear here once the user completes their first shift"
              icon={
                <svg className="h-12 w-12 text-amber-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={0.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
